import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import SeoHead from '@/components/seo/SeoHead';

export default function CliniciansPage() {
  return (
    <PageLayout>
      <SeoHead
        title="AI Clinical Notes for Individual Clinicians — Coming Soon | Notematic"
        description="A solo clinician plan for AI-assisted mental health documentation is planned for Australia. Register your interest to be notified when individual access opens."
        canonical="/solutions/clinicians"
      />
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-white to-secondary-50 dark:from-secondary-900 dark:to-secondary-800">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 text-sm font-medium mb-8">
            Coming later — Individual clinicians
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-6">
            AI-assisted documentation for{' '}
            <span className="text-gradient">individual clinicians</span>
          </h1>
          <p className="text-lg text-secondary-600 dark:text-secondary-300 mb-10">
            A solo clinician plan is planned after self-serve onboarding and billing infrastructure
            is production-ready. We are focused on MMHC and service-level rollouts now.
            Register your interest to be notified when we open individual access.
          </p>
          <Link to="/contact" className="btn btn-primary btn-lg">Register Your Interest</Link>
        </div>
      </section>

      {/* What to expect */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white text-center mb-10">
            What the solo plan will offer
          </h2>
          <div className="space-y-4">
            {[
              { label: 'Estimated price', value: 'AUD 249 / month', note: 'Subject to change before launch' },
              { label: 'Sessions', value: 'Up to 40 sessions / month', note: 'Standard clinical use' },
              { label: 'Workflows', value: '7 clinical note formats', note: 'IAR, SOAP, DAP, BIRP, GIRP, PIRP & PIE — AI-assisted draft, clinician review and approval' },
              { label: 'Infrastructure', value: 'Australian-hosted on Microsoft Azure', note: 'Same platform as organisational plans' },
              { label: 'Availability', value: 'Coming later', note: 'After organisational rollout infrastructure is established' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col sm:flex-row sm:items-center justify-between rounded-xl bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 px-6 py-4 gap-2">
                <div>
                  <div className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">{item.label}</div>
                  <div className="font-semibold text-secondary-900 dark:text-white">{item.value}</div>
                </div>
                <div className="text-xs text-secondary-400 dark:text-secondary-500 sm:text-right">{item.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Messaging guardrail - honest future framing */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <div className="rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-8">
            <h3 className="font-semibold text-secondary-900 dark:text-white mb-3">
              Why isn't it available now?
            </h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
              We are prioritising safe, well-supported organisational rollouts first. Building a
              reliable self-serve solo offering requires billing, onboarding, and support
              infrastructure that we are not rushing. Individual clinicians deserve the same quality
              and governance discipline as larger services — we will not launch until it is ready.
            </p>
            <Link
              to="/solutions/mmhc"
              className="text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline"
            >
              Interested in an organisational plan? See our MMHC solutions →
            </Link>
          </div>
        </div>
      </section>

      {/* Register interest CTA */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-xl mx-auto text-center">
          <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-3">
            Get notified when individual access opens
          </h2>
          <p className="text-secondary-500 dark:text-secondary-400 text-sm mb-6">
            Register your interest via the contact form. We will notify you when individual
            clinician access is available.
          </p>
          <Link to="/contact" className="btn btn-primary btn-md">Register Interest</Link>
        </div>
      </section>
    </PageLayout>
  );
}

